import type { OtimAPIClient } from "@api/api-client";
import type {
  TransactionGetListRequest,
  TransactionGetListResponse,
} from "@api/schemas/transaction";
import type { Sort } from "@schemas/misc";
import type { Address, WalletClient } from "viem";

export interface GetTransactionListOptions {
  address?: Address;
  chainId?: number;
  instructionIds: string[];
  from: string;
  to: string;
  skip?: number;
  take?: number;
  sort?: Sort;
}

export async function getList(
  client: OtimAPIClient,
  walletClient: WalletClient,
  {
    address,
    chainId,
    instructionIds,
    from,
    to,
    skip = 0,
    take = 20,
    sort = "desc",
  }: GetTransactionListOptions,
): Promise<TransactionGetListResponse> {
  if (!address && !walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  if (!chainId && !walletClient.chain) {
    throw new Error("No chain available on wallet client");
  }

  const targetAddress = address || walletClient.account!.address;
  const targetChainId = chainId || walletClient.chain!.id;

  const requestBody: TransactionGetListRequest = {
    address: targetAddress,
    chainId: targetChainId,
    instructionIds,
    from,
    to,
    skip,
    take,
    sort,
  };

  const response = await client.transaction.getAll(requestBody);
  return response.data;
}

export interface TransactionClient {
  readonly getList: (
    options: GetTransactionListOptions,
  ) => ReturnType<typeof getList>;
}

export function createTransactionClient(
  client: OtimAPIClient,
  walletClient: WalletClient,
): TransactionClient {
  return {
    getList: (options: GetTransactionListOptions) =>
      getList(client, walletClient, options),
  };
}
